import React, { memo } from "react";
import { validators, formats } from "investira.sdk";
import { Stack, useTheme } from "investira.react.components";

const CorrelacaoCenarioTooltip = memo((props) => {
  const { variacoes } = props;

  const theme = useTheme();

  const neutral = theme.palette.background.paper;
  const neg = theme.palette.error.light;
  const pos = theme.palette.primary.main;

  const neutralRgb = parseColorToRgb(neutral);
  const negRgb = parseColorToRgb(neg);
  const posRgb = parseColorToRgb(pos);

  function parseColorToRgb(input) {
    if (!input) return { r: 0, g: 0, b: 0 };
    if (input.startsWith("#")) {
      let c = input.slice(1);
      if (c.length === 3)
        c = c
          .split("")
          .map((ch) => ch + ch)
          .join("");
      const num = parseInt(c, 16);
      return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    }
    const m = input.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (m) return { r: +m[1], g: +m[2], b: +m[3] };
    return { r: 0, g: 0, b: 0 };
  }

  function rgbToCss({ r, g, b }) {
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  }

  function mixRgb(a, b, t) {
    return {
      r: a.r + (b.r - a.r) * t,
      g: a.g + (b.g - a.g) * t,
      b: a.b + (b.b - a.b) * t,
    };
  }

  const getCellColor = (pValue, pRange = 100) => {
    if (pValue === null || pValue === undefined) return rgbToCss(neutralRgb);
    // Normaliza o valor para a escala de 0 a 1, dividindo pelo range máximo
    // Se pRange for 0 ou não fornecido, usa 100 como padrão
    const xMaxRange = pRange > 0 ? pRange : 100;
    const xNormalizedValue = Math.min(1, Math.abs(pValue) / xMaxRange);
    if (pValue > 0)
      return rgbToCss(mixRgb(neutralRgb, posRgb, xNormalizedValue));
    return rgbToCss(mixRgb(neutralRgb, negRgb, xNormalizedValue));
  };

  const xAtivoCorrelacoes = Object.values(variacoes);

  // Calcula o maior valor absoluto já multiplicado por 100 (mesmo formato usado na função getCellColor)
  const xMaiorValorAbsoluto =
    xAtivoCorrelacoes && xAtivoCorrelacoes.length
      ? Math.max(
          ...xAtivoCorrelacoes.map((xItem) =>
            Math.abs((xItem.fator - 1 ?? 0) * 100)
          )
        )
      : 100; // Se não houver valores, usa 100 como padrão

  return (
    <Stack gap={0.5}>
      {(() => {
        if (!xAtivoCorrelacoes) return null;
        // Ordena as correlações por id, sem mutar o original
        const xCorrelacoesSorted = xAtivoCorrelacoes.slice().sort((xA, xB) => {
          if (xA.fator - 1 > xB.fator - 1) return -1;
          if (xA.fator - 1 < xB.fator - 1) return 1;
          return 0;
        });
        // Agrupa duas a duas
        const xPairs = [];
        for (let xIndex = 0; xIndex < xCorrelacoesSorted.length; xIndex += 2) {
          xPairs.push([
            xCorrelacoesSorted[xIndex],
            xCorrelacoesSorted[xIndex + 1] || null, // Pode ser undefined se ímpar, então põe null
          ]);
        }
        return xPairs.map((xPair, xPairIndex) => (
          <Stack key={xPairIndex} direction="row" gap={2}>
            {xPair.map((xCorrelacoes, xColIndex) => {
              if (!xCorrelacoes) {
                // Espaço vazio se não houver par
                return <Stack key={xColIndex} flex={1} />;
              }
              const xCorrelacaoValue =
                xCorrelacoes.fator === null || xCorrelacoes.fator === undefined
                  ? "---"
                  : xCorrelacoes.fator === 1
                  ? "1"
                  : xCorrelacoes.fator === -1
                  ? "-1"
                  : formats.formatNumber((xCorrelacoes.fator - 1) * 100, 4);

              const xBenchmark = xCorrelacoes.indicador_id;

              return (
                <Stack
                  key={xCorrelacoes.id}
                  direction="row"
                  gap={1}
                  flex={1}
                  //   sx={{ minWidth: "100px" }}
                >
                  <Stack
                    flex={1}
                    sx={{ minWidth: "50px", p: 0.5, whiteSpace: "nowrap" }}
                  >
                    {xBenchmark}
                  </Stack>
                  <Stack
                    id={xCorrelacoes.id}
                    flex={1}
                    sx={{
                      minWidth: "50px",
                      backgroundColor: getCellColor(
                        (xCorrelacoes.fator - 1) * 100,
                        xMaiorValorAbsoluto
                      ),
                      p: 0.5,
                      borderRadius: 1,
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      color: validators.isNull(xCorrelacoes.fator)
                        ? theme.palette.text.secondary
                        : (theme) =>
                            theme.palette.getContrastText(
                              getCellColor(
                                (xCorrelacoes.fator - 1) * 100,
                                xMaiorValorAbsoluto
                              )
                            ),
                    }}
                  >
                    {xCorrelacaoValue}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        ));
      })()}
    </Stack>
  );
});

export default CorrelacaoCenarioTooltip;
